import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ABTest } from '@freelancer/abtest';
import { RedirectToPhp } from '@freelancer/redirect-to-php';
import { Tracking } from '@freelancer/tracking';
import * as Rx from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { LoginServiceInterface } from './login.interface';

/**
 * This service can be injected as a login service.
 * It redirects to the gaf/login.php page unless `w=t` is set.
 * In future this may just become a `NoRedirectLogin` service.
 */
@Injectable({
  providedIn: 'root',
})
export class Login implements LoginServiceInterface {
  constructor(
    private redirectToPhp: RedirectToPhp,
    private abtest: ABTest,
    @Inject(PLATFORM_ID) private platformId: Object,
    private tracking: Tracking,
  ) {}

  redirect(
    next?: string,
    routeSnapshot?: ActivatedRouteSnapshot,
  ): Rx.Observable<boolean> {
    if (routeSnapshot && routeSnapshot.queryParams.w === 't') {
      return Rx.of(true);
    }

    if (isPlatformServer(this.platformId)) {
      return Rx.from(
        this.abtest
          .handleServer()
          .then(() =>
            this.redirectToPhp
              .doRedirect(next ? `/login?next=${next}` : '/login')
              .toPromise(),
          ),
      );
    }

    if (this.abtest.isWhitelistUser()) {
      return Rx.of(true);
    }

    return this.tracking.getSessionId().pipe(
      switchMap(sessionId => {
        if (
          this.abtest.shouldEnrol(
            'T140960-webapp-login-signup-v2',
            sessionId,
            0.05,
          )
        ) {
          // https://www.freelancer.com/admin/abtest/index.php?action=show_experiment&experiment_id=1067
          return this.abtest
            .getSessionExperimentVariation('T140960-webapp-login-signup-v2')
            .pipe(
              switchMap(variation => {
                if (variation === 'test') {
                  return Rx.of(true);
                }
                return this.loginRedirectToPhp(next);
              }),
            );
        }

        return this.loginRedirectToPhp(next);
      }),
    );
  }

  loginRedirectToPhp(next?: string) {
    if (next) {
      return this.redirectToPhp.doRedirect(`/login?next=${next}`);
    }
    return this.redirectToPhp.doRedirect('/login');
  }
}
